<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Left Illustration -->
      <b-col
        lg="8"
        class="d-none d-lg-flex align-items-center p-5 auth-illustration-wrapper"
      >
        <div class="w-100 d-lg-flex align-items-center justify-content-center px-5">
          <b-img
            fluid
            :src="imgUrl"
            alt="Access Key Illustration"
            class="auth-illustration"
          />
        </div>
      </b-col>

      <!-- Right Form Column -->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto py-2"
        >
          <!-- Top Row: Language Selector -->
          <div class="d-flex justify-content-end mb-2">
            <b-dropdown
              variant="link"
              no-caret
              right
              toggle-class="p-0 text-decoration-none"
            >
              <template #button-content>
                <div class="locale-toggle-btn shadow-none">
                  <b-img
                    :src="currentLocale.img"
                    height="14px"
                    width="20px"
                    :alt="currentLocale.locale"
                    class="mr-50 rounded-sm"
                  />
                  <span class="font-small-2 font-weight-bold text-body mr-25">{{ currentLocale.name }}</span>
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="14"
                    class="text-muted"
                  />
                </div>
              </template>
              <b-dropdown-item
                v-for="localeObj in locales"
                :key="localeObj.locale"
                :active="localeObj.locale === $i18n.locale"
                @click="changeLocale(localeObj.locale)"
              >
                <b-img
                  :src="localeObj.img"
                  height="14px"
                  width="20px"
                  :alt="localeObj.locale"
                  class="mr-50 rounded-sm"
                />
                <span class="font-small-2">{{ localeObj.name }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>

          <!-- Brand Logo -->
          <div class="text-center mb-2">
            <b-link
              v-show="!loadingLogo"
              class="brand-logo d-inline-block"
            >
              <b-img
                fluid
                :src="resolveLogo()"
                alt="Logo"
                class="brand-logo-img"
              />
            </b-link>
            <div
              v-show="loadingLogo"
              style="height: 3rem;"
            />
          </div>

          <!-- Header / Badge -->
          <div class="text-center mb-2">
            <div class="access-key-badge mb-1 shadow-sm">
              <feather-icon
                icon="KeyIcon"
                size="26"
              />
            </div>
            <b-card-title class="font-weight-bolder mb-50">
              {{ $t('Request Access Key') }}
            </b-card-title>
            <b-card-text class="text-muted font-small-3 mb-0">
              {{ $t('Enter your email and we will send your access key.') }}
            </b-card-text>
          </div>

          <!-- Form -->
          <validation-observer
            ref="simpleRules"
            #default="{ invalid }"
          >
            <b-form
              class="auth-forgot-password-form mt-2"
              @submit.prevent="validationForm"
            >
              <!-- Email Input -->
              <b-form-group
                :label="$t('Email')"
                label-for="forgot-password-email"
                class="mb-2"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  rules="required|email"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-input-group-prepend is-text>
                      <feather-icon icon="MailIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="forgot-password-email"
                      v-model="userEmail"
                      type="email"
                      name="forgot-password-email"
                      :state="errors.length > 0 ? false : null"
                      placeholder="usuario@empresa.com"
                      autofocus
                      @keyup.enter="validationForm"
                    />
                  </b-input-group>
                  <small class="text-danger d-block mt-25">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <!-- Submit Button -->
              <b-button
                type="submit"
                variant="primary"
                block
                size="lg"
                class="font-weight-bolder btn-submit"
                :disabled="invalid || loading || !userEmail"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <span>{{ loading ? $t('Sending...') : $t('Send Access Key') }}</span>
              </b-button>
            </b-form>
          </validation-observer>

          <!-- Divider -->
          <div class="divider my-2">
            <div class="divider-text font-small-2 text-uppercase">
              {{ $t('or') }}
            </div>
          </div>

          <!-- Shortcut to validation page if user already has key -->
          <b-card-text class="text-center mt-2 mb-1">
            <span class="text-muted font-small-3 mr-25">{{ $t('Do you already have an access key?') }}</span>
            <b-link
              :to="{ name: 'auth-acess-key' }"
              class="font-small-3 font-weight-bolder text-primary d-inline-flex align-items-center"
            >
              <span>{{ $t('Enter access key') }}</span>
              <feather-icon
                icon="ArrowRightIcon"
                size="14"
                class="ml-25"
              />
            </b-link>
          </b-card-text>

          <!-- Back to login link -->
          <p class="text-center mt-2 mb-0">
            <b-link
              :to="{ name: 'auth-login' }"
              class="d-inline-flex align-items-center font-weight-bold"
            >
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
                class="mr-25"
              />
              <span>{{ $t('Back to login') }}</span>
            </b-link>
          </p>

        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
/* eslint-disable import/no-cycle */
import {
  ValidationProvider,
  ValidationObserver,
} from 'vee-validate'
import {
  BRow,
  BCol,
  BLink,
  BCardTitle,
  BCardText,
  BImg,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BButton,
  BSpinner,
  BDropdown,
  BDropdownItem,
  VBTooltip,
} from 'bootstrap-vue'
import {
  required,
  email,
} from '@validations'
import store from '@/store/index'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import customerSettigns from '@core/utils/customerSettigns'
import systemStoreModule from '@core/layouts/components/app-navbar/systemStoreModule'
import { onUnmounted } from '@vue/composition-api'

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BImg,
    BForm,
    BButton,
    BSpinner,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BCardTitle,
    BCardText,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      required,
      email,

      userEmail: '',
      sideImg: require('@/assets/images/pages/forgot-password-v2.svg'),
      userSettings: null,
      loadingLogo: true,
      loading: false,

      previousAppLocale: null,

      locales: [
        {
          locale: 'pt',
          img: require('@/assets/images/flags/br.png'),
          name: 'Português',
        },
        {
          locale: 'en',
          img: require('@/assets/images/flags/en.png'),
          name: 'English',
        },
        {
          locale: 'es',
          img: require('@/assets/images/flags/ar.png'),
          name: 'Español',
        },
      ],
    }
  },
  computed: {
    currentLocale() {
      return this.locales.find(l => l.locale === this.$i18n.locale) || this.locales[0]
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        return require('@/assets/images/pages/forgot-password-v2-dark.svg')
      }
      return this.sideImg
    },
  },
  created() {
    this.previousAppLocale = this.$i18n.locale
    this.$i18n.locale = 'pt'
  },
  mounted() {
    if (localStorage.userEmail) {
      this.userEmail = localStorage.userEmail
    }
    this.getSetings()
  },
  beforeDestroy() {
    // Restaura idioma anterior se sair sem ir para auth-acess-key
    if (this.$route.name !== 'auth-acess-key' && this.previousAppLocale) {
      this.$i18n.locale = this.previousAppLocale
    }
  },
  methods: {
    changeLocale(locale) {
      this.$i18n.locale = locale
    },
    async getSetings() {
      this.loadingLogo = true
      this.userSettings = await customerSettigns()
      this.loadingLogo = false
    },
    resolveLogo() {
      if (!this.userSettings) {
        return require('@/assets/images/logo/LogoExtend.png')
      }
      return this.userSettings.logoUrl
    },
    async validationForm() {
      const success = await this.$refs.simpleRules.validate()
      if (!success || this.loading) return

      this.loading = true
      try {
        await axios.post('/JWT/RequestAccessKey', {
          email: this.userEmail.trim(),
        })

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Code Sent'),
            icon: 'MailIcon',
            variant: 'success',
            text: this.$t('Access key sent to your email.'),
          },
        })

        localStorage.userEmail = this.userEmail.trim()
        this.$router.push({ name: 'auth-acess-key' })
      } catch (error) {
        const errorMsg = error?.response?.data?.message || this.$t('Failed to send email.')
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: errorMsg,
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
  setup() {
    const SYSTEM_APP_STORE_MODULE_NAME = 'app-system'

    // Register module
    if (!store.hasModule(SYSTEM_APP_STORE_MODULE_NAME)) store.registerModule(SYSTEM_APP_STORE_MODULE_NAME, systemStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SYSTEM_APP_STORE_MODULE_NAME)) store.unregisterModule(SYSTEM_APP_STORE_MODULE_NAME)
    })
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.brand-logo-img {
  max-height: 48px;
  max-width: 65%;
  object-fit: contain;
}

.locale-toggle-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  background-color: #f8f8f8;
  border: 1px solid #ebe9f1;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    border-color: #d8d6de;
  }
}

.access-key-badge {
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}

.btn-submit {
  height: 2.75rem;
  font-size: 1rem;
}

.auth-illustration-wrapper {
  background-color: #f8f8f8;
}

.auth-illustration {
  max-height: 520px;
}

// Dark mode overrides
.dark-layout {
  .auth-illustration-wrapper {
    background-color: #161d31;
  }

  .locale-toggle-btn {
    background-color: #283046;
    border-color: #3b4253;

    &:hover {
      background-color: #343d55;
    }
  }
}
</style>
